/**
 * Multi-row import modal.
 *
 * Spreadsheet-like editable grid: one column per editable column on
 * the target table (identity / computed columns omitted, since SQL
 * fills those). Type-aware cells, per-cell validation, paste support
 * for TSV / CSV from a spreadsheet (position-based — the user's
 * columns must align with the grid's column order).
 *
 * Submit calls POST /api/tables/<schema>/<name>/rows/bulk which is
 * transactional all-or-nothing. On failure the server returns the
 * offending rowIndex (and where possible the offending column name);
 * we highlight the row in red and show the SQL message on a banner so
 * the user can fix the data and resubmit.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconPlus, IconTrash, IconX } from "@tabler/icons-react";

import type { ApiClient } from "../api/client";
import type { ColumnMeta } from "../api/types";
import { parseClipboardData } from "../lib/clipboard-parse";
import { withDb } from "../lib/active-db";

const INITIAL_ROWS = 10;
const MAX_ROWS = 1000;

const NUMERIC_TYPES = new Set([
  "int", "bigint", "smallint", "tinyint",
  "decimal", "numeric", "money", "smallmoney",
  "float", "real",
]);
const DATE_TYPES = new Set([
  "date", "time", "datetime", "datetime2", "smalldatetime", "datetimeoffset",
]);

type Cell = string;
type Row = Record<string, Cell>;
type CellErrors = Record<string, string>;

export function ImportModal({
  api,
  schema,
  name,
  columns,
  dbId,
  opened,
  onClose,
  onSuccess,
}: {
  api: ApiClient;
  schema: string;
  name: string;
  columns: ColumnMeta[];
  dbId: string | undefined;
  opened: boolean;
  onClose: () => void;
  onSuccess: (insertedCount: number) => void;
}) {
  // Only insertable columns get a grid column. The server enforces the
  // same allowlist, but pre-filtering here keeps the UI focused.
  const editableColumns = useMemo(
    () => columns.filter((c) => !c.isIdentity && !c.isComputed),
    [columns],
  );

  const [rows, setRows] = useState<Row[]>(() => makeEmptyRows(INITIAL_ROWS, editableColumns));
  const [errors, setErrors] = useState<Record<number, CellErrors>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<{
    rowIndex: number | null;
    column: string | null;
    message: string;
  } | null>(null);
  const focusRef = useRef<{ row: number; col: number } | null>(null);

  // Reset state every time the modal is opened.
  useEffect(() => {
    if (opened) {
      setRows(makeEmptyRows(INITIAL_ROWS, editableColumns));
      setErrors({});
      setServerError(null);
    }
  }, [opened, editableColumns]);

  const setCell = useCallback(
    (rowIdx: number, col: ColumnMeta, value: string) => {
      setRows((prev) => {
        const next = prev.slice();
        next[rowIdx] = { ...(next[rowIdx] ?? {}), [col.name]: value };
        return next;
      });
    },
    [],
  );

  const validateCell = useCallback((col: ColumnMeta, raw: string): string | null => {
    const value = raw.trim();
    if (value === "") return null; // empty → NULL, server decides if that's OK

    const dt = col.dataType.toLowerCase();
    if (dt === "bit") {
      if (!/^(true|false|1|0|yes|no|t|f|y|n)$/i.test(value)) {
        return "Use true/false, 1/0, or yes/no";
      }
    } else if (NUMERIC_TYPES.has(dt)) {
      if (!/^-?\d+(\.\d+)?$/.test(value)) return "Not a number";
    } else if (DATE_TYPES.has(dt)) {
      // Lenient — server's SQL conversion is the real authority.
      // Catch obvious garbage but accept any string Date.parse takes.
      const parsed = Date.parse(value);
      if (Number.isNaN(parsed)) return "Not a valid date/time";
    } else if (
      (dt.startsWith("nvarchar") || dt.startsWith("varchar") ||
       dt.startsWith("char") || dt.startsWith("nchar")) &&
      col.maxLength != null && col.maxLength > 0 && value.length > col.maxLength
    ) {
      return `Too long (max ${col.maxLength} chars)`;
    }
    return null;
  }, []);

  const onCellBlur = useCallback(
    (rowIdx: number, col: ColumnMeta, raw: string) => {
      const err = validateCell(col, raw);
      setErrors((prev) => {
        const rowErr = { ...(prev[rowIdx] ?? {}) };
        if (err) rowErr[col.name] = err;
        else delete rowErr[col.name];
        return { ...prev, [rowIdx]: rowErr };
      });
    },
    [validateCell],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const raw = e.clipboardData.getData("text/plain");
      if (!raw) return;
      const grid = parseClipboardData(raw);
      // If it's a single cell, let the browser handle the paste natively.
      if (grid.length === 1 && grid[0]!.length === 1) return;
      e.preventDefault();
      const start = focusRef.current ?? { row: 0, col: 0 };
      setRows((prev) => applyPaste(prev, editableColumns, start, grid));
    },
    [editableColumns],
  );

  const addRow = () => {
    setRows((prev) =>
      prev.length >= MAX_ROWS ? prev : [...prev, makeEmptyRow(editableColumns)],
    );
  };

  const removeRow = (rowIdx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== rowIdx));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[rowIdx];
      // Renumber the keys since indices shifted.
      const renumbered: Record<number, CellErrors> = {};
      Object.entries(next).forEach(([k, v]) => {
        const i = Number(k);
        renumbered[i > rowIdx ? i - 1 : i] = v;
      });
      return renumbered;
    });
  };

  // What we'll actually send: drop fully-empty rows (every cell blank),
  // since the user typically leaves spare rows below their data.
  const filledRows = useMemo(
    () => rows.filter((r) => Object.values(r).some((v) => (v ?? "").trim() !== "")),
    [rows],
  );

  // Eager pre-submit validation across all filled rows.
  const totalErrors = useMemo(() => {
    let n = 0;
    for (const row of rows) {
      if (!Object.values(row).some((v) => (v ?? "").trim() !== "")) continue;
      for (const col of editableColumns) {
        if (validateCell(col, row[col.name] ?? "")) n++;
      }
    }
    return n;
  }, [rows, editableColumns, validateCell]);

  async function submit() {
    setSubmitting(true);
    setServerError(null);

    // Final validation pass — populate errors for any cell we hadn't blurred.
    const newErrors: Record<number, CellErrors> = {};
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      if (!Object.values(row).some((v) => (v ?? "").trim() !== "")) continue;
      const rowErr: CellErrors = {};
      for (const col of editableColumns) {
        const err = validateCell(col, row[col.name] ?? "");
        if (err) rowErr[col.name] = err;
      }
      if (Object.keys(rowErr).length) newErrors[i] = rowErr;
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setSubmitting(false);
      notifications.show({
        message: "Fix the highlighted cells before importing.",
        color: "red",
      });
      return;
    }

    if (filledRows.length === 0) {
      setSubmitting(false);
      notifications.show({ message: "Nothing to import — add at least one row.", color: "yellow" });
      return;
    }

    // Convert cells to typed values for the API.
    const payload = filledRows.map((row) => coerceRowForApi(row, editableColumns));

    try {
      const res = await api.post<{ inserted: number }>(
        withDb(`/api/tables/${schema}/${name}/rows/bulk`, dbId),
        { rows: payload },
      );
      notifications.show({
        message: `Imported ${res.inserted} row${res.inserted === 1 ? "" : "s"}.`,
        color: "teal",
      });
      onSuccess(res.inserted);
      onClose();
    } catch (err) {
      // The API client throws an Error with a `payload` field when the
      // response is JSON. Pull rowIndex / failedColumn / message out so
      // we can highlight precisely.
      const e = err as { payload?: { rowIndex?: number; failedColumn?: string; message?: string }; message?: string };
      const payload = e.payload ?? {};
      setServerError({
        rowIndex: typeof payload.rowIndex === "number" ? payload.rowIndex : null,
        column: payload.failedColumn ?? null,
        message: payload.message ?? e.message ?? "Import failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="95%"
      centered
      padding={0}
      radius="md"
      closeOnClickOutside={false}
      overlayProps={{ blur: 3, backgroundOpacity: 0.35 }}
      classNames={{ body: "kse-import-modal__body" }}
    >
      <div className="kse-import-modal">
        <header className="kse-import-modal__header">
          <div>
            <Text fw={700} fz="lg">Import rows · {schema}.{name}</Text>
            <Text size="sm" c="dimmed">
              Paste TSV / CSV from your spreadsheet, or type rows directly.
              Position-based mapping — the columns in your paste must align
              with the grid below. Identity and computed columns are omitted.
            </Text>
          </div>
          <Tooltip label="Close">
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close import"
            >
              <IconX size={18} />
            </ActionIcon>
          </Tooltip>
        </header>

        {serverError && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Import failed — nothing was inserted"
            mx="md"
            mt="sm"
          >
            {serverError.rowIndex != null && (
              <Text size="sm" mb={4}>
                <strong>Row {serverError.rowIndex + 1}</strong>
                {serverError.column ? <> · column <code>{serverError.column}</code></> : null}
              </Text>
            )}
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{serverError.message}</Text>
          </Alert>
        )}

        <div className="kse-import-grid-scroll" onPaste={onPaste}>
          <table className="kse-import-grid">
            <thead>
              <tr>
                <th className="kse-import-grid__rownum"></th>
                {editableColumns.map((c) => (
                  <th key={c.name}>
                    <div className="kse-import-grid__colhead">
                      {!c.isNullable && (
                        <span
                          className="kse-import-grid__required"
                          title="NOT NULL — a value is required"
                          aria-label="required"
                        >
                          *
                        </span>
                      )}
                      <span>{c.name}</span>
                    </div>
                  </th>
                ))}
                <th className="kse-import-grid__actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => {
                const rowErr = errors[rowIdx];
                const isServerFailedRow = serverError?.rowIndex === rowIdx;
                return (
                  <tr
                    key={rowIdx}
                    className={
                      "kse-import-grid__row" +
                      (isServerFailedRow ? " kse-import-grid__row--server-error" : "")
                    }
                  >
                    <td className="kse-import-grid__rownum">{rowIdx + 1}</td>
                    {editableColumns.map((col, colIdx) => {
                      const value = row[col.name] ?? "";
                      const cellErr =
                        rowErr?.[col.name] ??
                        (isServerFailedRow && serverError?.column === col.name
                          ? serverError.message
                          : undefined);
                      return (
                        <td key={col.name} className={cellErr ? "kse-import-grid__cell--err" : undefined}>
                          {col.dataType.toLowerCase() === "bit" ? (
                            <input
                              type="text"
                              className="kse-import-grid__input"
                              value={value}
                              onChange={(e) => setCell(rowIdx, col, e.currentTarget.value)}
                              onBlur={(e) => onCellBlur(rowIdx, col, e.currentTarget.value)}
                              onFocus={() => { focusRef.current = { row: rowIdx, col: colIdx }; }}
                              placeholder="true / false"
                              data-row={rowIdx}
                              data-col={colIdx}
                            />
                          ) : (
                            <input
                              type="text"
                              className="kse-import-grid__input"
                              value={value}
                              onChange={(e) => setCell(rowIdx, col, e.currentTarget.value)}
                              onBlur={(e) => onCellBlur(rowIdx, col, e.currentTarget.value)}
                              onFocus={() => { focusRef.current = { row: rowIdx, col: colIdx }; }}
                              data-row={rowIdx}
                              data-col={colIdx}
                            />
                          )}
                          {cellErr && (
                            <Tooltip label={cellErr} multiline maw={280}>
                              <span className="kse-import-grid__cell-err-dot" aria-label={cellErr}>!</span>
                            </Tooltip>
                          )}
                        </td>
                      );
                    })}
                    <td className="kse-import-grid__actions">
                      <Tooltip label="Remove row">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => removeRow(rowIdx)}
                          disabled={submitting || rows.length === 1}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <footer className="kse-import-modal__footer">
          <Group gap="xs">
            <Button
              variant="default"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={addRow}
              disabled={submitting || rows.length >= MAX_ROWS}
            >
              Add row
            </Button>
            <Text size="xs" c="dimmed">
              {filledRows.length} of {rows.length} rows have data
              {totalErrors > 0 && <> · <span style={{ color: "var(--mantine-color-red-7)" }}>{totalErrors} cell error{totalErrors === 1 ? "" : "s"}</span></>}
            </Text>
          </Group>
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submit}
              loading={submitting}
              disabled={filledRows.length === 0 || totalErrors > 0}
            >
              Import {filledRows.length || 0} row{filledRows.length === 1 ? "" : "s"}
            </Button>
          </Group>
        </footer>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function makeEmptyRow(columns: ColumnMeta[]): Row {
  const r: Row = {};
  for (const c of columns) r[c.name] = "";
  return r;
}
function makeEmptyRows(n: number, columns: ColumnMeta[]): Row[] {
  return Array.from({ length: n }, () => makeEmptyRow(columns));
}

/**
 * Apply a 2-D pasted grid into the rows starting at (startRow, startCol),
 * extending the rows array if the paste runs past the bottom.
 */
function applyPaste(
  rows: Row[],
  columns: ColumnMeta[],
  start: { row: number; col: number },
  data: string[][],
): Row[] {
  const next = rows.slice();
  for (let r = 0; r < data.length; r++) {
    const targetRow = start.row + r;
    while (next.length <= targetRow) {
      if (next.length >= MAX_ROWS) break;
      next.push(makeEmptyRow(columns));
    }
    if (targetRow >= MAX_ROWS) break;
    const cells = data[r]!;
    const merged: Row = { ...next[targetRow]! };
    for (let c = 0; c < cells.length; c++) {
      const colIdx = start.col + c;
      if (colIdx >= columns.length) break;
      merged[columns[colIdx]!.name] = cells[c]!;
    }
    next[targetRow] = merged;
  }
  return next;
}

/**
 * Convert a UI row (all string values) to the typed values the API
 * expects. Empty strings become null. The API also normalises empty →
 * null but doing it here keeps the wire payload tidy.
 */
function coerceRowForApi(row: Row, columns: ColumnMeta[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const col of columns) {
    const raw = (row[col.name] ?? "").trim();
    if (raw === "") {
      out[col.name] = null;
      continue;
    }
    const dt = col.dataType.toLowerCase();
    if (dt === "bit") {
      out[col.name] = /^(true|1|yes|t|y)$/i.test(raw);
    } else if (NUMERIC_TYPES.has(dt)) {
      // Send as number where it parses cleanly; otherwise leave as string
      // (server-side conversion will give a clearer error).
      const n = Number(raw);
      out[col.name] = Number.isFinite(n) ? n : raw;
    } else {
      out[col.name] = raw;
    }
  }
  return out;
}

